<div class="project-detail-page">

  <!-- Loading -->
  <div class="loading-center" *ngIf="loading()">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading project…</p>
  </div>

  <!-- Error -->
  <div class="error-banner" *ngIf="error() && !loading()">
    <mat-icon>error_outline</mat-icon>
    {{ error() }}
    <button mat-icon-button (click)="error.set(null)"><mat-icon>close</mat-icon></button>
  </div>

  <ng-container *ngIf="!loading() && project() as p">

    <app-breadcrumb [crumbs]="projectBreadcrumbs(p)" />

    <!-- Back nav + actions -->
    <div class="page-header">
      <div class="header-left">
        <button mat-icon-button (click)="goBack()" matTooltip="Back to projects">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <div class="title-block">
          <div class="title-row">
            <div class="color-dot" [style.background-color]="p.color || '#6366f1'"></div>
            <h1 class="project-name">{{ p.name }}</h1>
            <span class="project-code" *ngIf="p.code">{{ p.code }}</span>
            <span class="status-badge archived-badge" *ngIf="p.archivedAt">Archived</span>
            <span class="status-badge template-badge" *ngIf="p.isTemplate">Template</span>
          </div>
          <p class="project-description" *ngIf="p.description">{{ p.description }}</p>
        </div>
      </div>
      <div class="header-actions">
        <button mat-icon-button (click)="toggleFavorite()" [disabled]="togglingFavorite()"
          [matTooltip]="p.isFavorite ? 'Remove from favorites' : 'Add to favorites'">
          <mat-icon [class.favorited]="p.isFavorite">{{ p.isFavorite ? 'star' : 'star_border' }}</mat-icon>
        </button>
        <button mat-stroked-button color="warn" *ngIf="!p.archivedAt" [disabled]="archiving()"
          (click)="archiveProject()">
          <mat-icon>archive</mat-icon>
          {{ archiving() ? 'Archiving…' : 'Archive' }}
        </button>
        <button mat-stroked-button color="primary" *ngIf="p.archivedAt" [disabled]="archiving()"
          (click)="restoreProject()">
          <mat-icon>unarchive</mat-icon>
          {{ archiving() ? 'Restoring…' : 'Restore' }}
        </button>
        <button mat-stroked-button [disabled]="duplicating()" (click)="duplicateProject()">
          <mat-icon>content_copy</mat-icon>
          {{ duplicating() ? 'Duplicating…' : 'Duplicate' }}
        </button>
        <button mat-stroked-button *ngIf="!p.archivedAt" [disabled]="settingTemplate()"
          (click)="toggleTemplate()">
          <mat-icon>{{ p.isTemplate ? 'dashboard_customize' : 'dashboard' }}</mat-icon>
          {{ settingTemplate() ? 'Saving…' : (p.isTemplate ? 'Unmark Template' : 'Mark as Template') }}
        </button>
        <button mat-raised-button color="primary" type="button" (click)="openAddTaskForm()">
          <mat-icon>add_task</mat-icon>
          Add Task
        </button>
        <button mat-stroked-button (click)="editProject()">
          <mat-icon>edit</mat-icon>
          Edit Project
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <mat-card class="stat-card tt-compact-card">
        <div class="stat-icon status">
          <mat-icon>lens</mat-icon>
        </div>
        <div class="stat-body">
          <div class="stat-value">
            <span class="status-badge" [ngClass]="statusColorMap[p.status]">
              {{ statusLabels[p.status] }}
            </span>
          </div>
          <div class="stat-label">Status</div>
        </div>
      </mat-card>

      <mat-card class="stat-card tt-compact-card">
        <div class="stat-icon members">
          <mat-icon>group</mat-icon>
        </div>
        <div class="stat-body">
          <div class="stat-value">{{ p.members.length }}</div>
          <div class="stat-label">Members</div>
        </div>
      </mat-card>

      <mat-card class="stat-card tt-compact-card">
        <div class="stat-icon tasks">
          <mat-icon>task_alt</mat-icon>
        </div>
        <div class="stat-body">
          <div class="stat-value">{{ p.tasksSummary.done || 0 }}/{{ p.tasksSummary.total || 0 }}</div>
          <div class="stat-label">Tasks Done</div>
        </div>
      </mat-card>

      <mat-card class="stat-card tt-compact-card">
        <div class="stat-icon progress">
          <mat-icon>trending_up</mat-icon>
        </div>
        <div class="stat-body">
          <div class="stat-value">{{ p.progress | number:'1.0-0' }}%</div>
          <div class="stat-label">Progress</div>
        </div>
      </mat-card>
    </div>

    <!-- Tabs -->
    <mat-tab-group class="detail-tabs" animationDuration="150ms"
      [selectedIndex]="selectedTabIndex()"
      (selectedIndexChange)="onTabChange($event)">

      <!-- Overview Tab -->
      <mat-tab label="Overview">
        <div class="tab-content">
          <div class="detail-grid">

            <!-- Project Settings -->
            <mat-card class="info-card">
              <mat-card-header>
                <mat-card-title>Project Settings</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="info-row">
                  <span class="info-label"><mat-icon>category</mat-icon> Category</span>
                  <span class="info-value">{{ p.category || '—' }}</span>
                </div>
                <mat-divider></mat-divider>
                <div class="info-row">
                  <span class="info-label"><mat-icon>tag</mat-icon> Issue Key Prefix</span>
                  <span class="info-value mono">{{ p.issueKeyPrefix || p.code || '—' }}</span>
                </div>
                <mat-divider></mat-divider>
                <div class="info-row">
                  <span class="info-label"><mat-icon>account_tree</mat-icon> Issue Workflow</span>
                  <span class="info-value">{{ workflowDisplayName() }}</span>
                </div>
                <mat-divider></mat-divider>
                <div class="info-row workflow-actions">
                  <mat-form-field appearance="outline" class="workflow-select">
                    <mat-label>Assign workflow</mat-label>
                    <mat-select [(ngModel)]="selectedWorkflowId">
                      <mat-option value="">None</mat-option>
                      <mat-option *ngFor="let w of projectWorkflows()" [value]="w.id">
                        {{ w.name }}{{ w.isDefault ? ' ★' : '' }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>
                  <button mat-stroked-button type="button"
                          [disabled]="assigningWorkflow()"
                          (click)="assignWorkflow()">
                    {{ assigningWorkflow() ? 'Saving…' : 'Save workflow' }}
                  </button>
                  <a mat-stroked-button [routerLink]="['/workflows']" [queryParams]="{ projectId: p.id }">
                    <mat-icon>settings</mat-icon> Manage workflow
                  </a>
                </div>
                <mat-divider></mat-divider>
                <div class="info-row">
                  <span class="info-label"><mat-icon>manage_accounts</mat-icon> Manager / Owner</span>
                  <span class="info-value">{{ p.ownerName || '—' }}</span>
                </div>
                <mat-divider *ngIf="p.archivedAt"></mat-divider>
                <div class="info-row" *ngIf="p.archivedAt">
                  <span class="info-label"><mat-icon>archive</mat-icon> Archived</span>
                  <span class="info-value">{{ p.archivedAt | date:'medium' }}</span>
                </div>
              </mat-card-content>
            </mat-card>

            <!-- Project Info -->
            <mat-card class="info-card">
              <mat-card-header>
                <mat-card-title>Project Details</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="info-row">
                  <span class="info-label"><mat-icon>flag</mat-icon> Priority</span>
                  <span class="info-value">{{ priorityLabels[p.priority] }}</span>
                </div>
                <mat-divider></mat-divider>
                <div class="info-row" *ngIf="p.accountName">
                  <span class="info-label"><mat-icon>business</mat-icon> Account</span>
                  <span class="info-value">{{ p.accountName }}</span>
                </div>
                <mat-divider *ngIf="p.accountName"></mat-divider>
                <div class="info-row" *ngIf="p.ownerName">
                  <span class="info-label"><mat-icon>person</mat-icon> Owner</span>
                  <span class="info-value">{{ p.ownerName }}</span>
                </div>
                <mat-divider *ngIf="p.ownerName"></mat-divider>
                <div class="info-row" *ngIf="p.startDate">
                  <span class="info-label"><mat-icon>calendar_today</mat-icon> Start</span>
                  <span class="info-value">{{ p.startDate | date:'mediumDate' }}</span>
                </div>
                <mat-divider *ngIf="p.startDate"></mat-divider>
                <div class="info-row" *ngIf="p.dueDate">
                  <span class="info-label"><mat-icon>event</mat-icon> Due</span>
                  <span class="info-value">{{ p.dueDate | date:'mediumDate' }}</span>
                </div>
                <mat-divider *ngIf="p.dueDate"></mat-divider>
                <div class="info-row" *ngIf="p.budget">
                  <span class="info-label"><mat-icon>attach_money</mat-icon> Budget</span>
                  <span class="info-value">{{ p.budget | currency }}</span>
                </div>
                <mat-divider *ngIf="p.budget"></mat-divider>
                <div class="info-row">
                  <span class="info-label"><mat-icon>schedule</mat-icon> Created</span>
                  <span class="info-value">{{ p.createdAt | date:'medium' }}</span>
                </div>
              </mat-card-content>
            </mat-card>

            <!-- Progress Card -->
            <mat-card class="info-card">
              <mat-card-header>
                <mat-card-title>Progress</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="big-progress">
                  <div class="progress-number">{{ p.progress | number:'1.0-0' }}%</div>
                  <div class="progress-bar-large">
                    <div class="progress-fill-large" [ngClass]="getProgressColor(p.progress)"
                      [style.width.%]="p.progress"></div>
                  </div>
                </div>

                <div class="task-breakdown" *ngIf="p.tasksSummary">
                  <div class="task-stat">
                    <span class="task-num">{{ p.tasksSummary.total }}</span>
                    <span class="task-lbl">Total</span>
                  </div>
                  <div class="task-stat open">
                    <span class="task-num">{{ p.tasksSummary.open }}</span>
                    <span class="task-lbl">Open</span>
                  </div>
                  <div class="task-stat inprogress">
                    <span class="task-num">{{ p.tasksSummary.inProgress }}</span>
                    <span class="task-lbl">In Progress</span>
                  </div>
                  <div class="task-stat done">
                    <span class="task-num">{{ p.tasksSummary.done }}</span>
                    <span class="task-lbl">Done</span>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>

            <!-- Published App Details -->
            <mat-card class="info-card" *ngIf="p.isPublishedApp">
              <mat-card-header>
                <mat-card-title>
                  <mat-icon>publish</mat-icon>
                  App Details
                </mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="info-row" *ngIf="p.platform">
                  <span class="info-label"><mat-icon>phone_android</mat-icon> Platform</span>
                  <span class="info-value">{{ p.platform }}</span>
                </div>
                <mat-divider *ngIf="p.platform"></mat-divider>
                <div class="info-row" *ngIf="p.packageName">
                  <span class="info-label"><mat-icon>apps</mat-icon> Package</span>
                  <span class="info-value mono">{{ p.packageName }}</span>
                </div>
                <mat-divider *ngIf="p.packageName"></mat-divider>
                <div class="info-row" *ngIf="p.storeUrl">
                  <span class="info-label"><mat-icon>store</mat-icon> Store URL</span>
                  <a [href]="p.storeUrl" target="_blank" class="info-value link">
                    {{ p.storeUrl }}
                    <mat-icon>open_in_new</mat-icon>
                  </a>
                </div>
              </mat-card-content>
            </mat-card>

          </div>
        </div>
      </mat-tab>

      <!-- Activity Tab -->
      <mat-tab label="Activity">
        <div class="tab-content">
          <mat-spinner *ngIf="loadingActivity()" diameter="28"></mat-spinner>
          <mat-card class="info-card" *ngIf="!loadingActivity() && activity().length">
            <mat-card-content>
              <div class="activity-row" *ngFor="let a of activity()">
                <strong>{{ a.activityTypeLabel }}</strong>
                <span class="muted">{{ a.occurredAt | date:'medium' }} · {{ a.actorName || 'System' }}</span>
                <p>{{ a.message }}</p>
                <mat-divider></mat-divider>
              </div>
            </mat-card-content>
          </mat-card>
          <div class="empty-members" *ngIf="!loadingActivity() && !activity().length">
            <mat-icon>history</mat-icon>
            <p>No project activity yet.</p>
          </div>
        </div>
      </mat-tab>

      <!-- Members Tab -->
      <mat-tab>
        <ng-template mat-tab-label>
          Members <span class="tab-badge">{{ p.members.length }}</span>
        </ng-template>
        <div class="tab-content">

          <!-- Add Member Form -->
          <mat-card class="add-member-card">
            <mat-card-header>
              <mat-card-title>Add Member</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="add-member-form">
                <mat-form-field appearance="outline" class="employee-field">
                  <mat-label>Select Employee</mat-label>
                  <mat-select [(ngModel)]="newMemberEmployeeId">
                    <mat-option *ngFor="let emp of availableEmployees()" [value]="emp.id">
                      {{ emp.name }}
                      <span class="emp-job" *ngIf="emp.jobTitle"> · {{ emp.jobTitle }}</span>
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline" class="role-field">
                  <mat-label>Role</mat-label>
                  <mat-select [(ngModel)]="newMemberRole">
                    <mat-option *ngFor="let r of memberRoles" [value]="r">{{ r }}</mat-option>
                  </mat-select>
                </mat-form-field>

                <button mat-raised-button color="primary"
                  [disabled]="!newMemberEmployeeId || addingMember()"
                  (click)="addMember()">
                  <mat-icon>person_add</mat-icon>
                  Add
                </button>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Members Table -->
          <mat-card class="members-card" *ngIf="p.members.length > 0">
            <table mat-table [dataSource]="p.members" class="members-table">

              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef>Member</th>
                <td mat-cell *matCellDef="let member">
                  <div class="member-info">
                    <div class="avatar">{{ member.employeeName.charAt(0) }}</div>
                    <div>
                      <div class="member-name">{{ member.employeeName }}</div>
                      <div class="member-email" *ngIf="member.employeeEmail">{{ member.employeeEmail }}</div>
                    </div>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="role">
                <th mat-header-cell *matHeaderCellDef>Role</th>
                <td mat-cell *matCellDef="let member">
                  <mat-select [(ngModel)]="member.role"
                    (ngModelChange)="updateMemberRole(member, $event)"
                    class="role-select">
                    <mat-option *ngFor="let r of memberRoles" [value]="r">{{ r }}</mat-option>
                  </mat-select>
                </td>
              </ng-container>

              <ng-container matColumnDef="joinedAt">
                <th mat-header-cell *matHeaderCellDef>Joined</th>
                <td mat-cell *matCellDef="let member">{{ member.joinedAt | date:'mediumDate' }}</td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let member">
                  <mat-slide-toggle
                    [checked]="member.isActive"
                    (change)="toggleMemberActive(member)"
                    color="primary">
                    {{ member.isActive ? 'Active' : 'Inactive' }}
                  </mat-slide-toggle>
                </td>
              </ng-container>

              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let member">
                  <button mat-icon-button color="warn"
                    matTooltip="Remove member"
                    (click)="removeMember(member)">
                    <mat-icon>person_remove</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="memberColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: memberColumns;"></tr>
            </table>
          </mat-card>

          <div class="empty-members" *ngIf="p.members.length === 0">
            <mat-icon>group_off</mat-icon>
            <p>No members yet. Add the first member above.</p>
          </div>

        </div>
      </mat-tab>

      <!-- Tasks Tab -->
      <mat-tab>
        <ng-template mat-tab-label>
          Tasks <span class="tab-badge">{{ tasks().length }}</span>
        </ng-template>
        <div class="tab-content">

          <div class="tasks-toolbar">
            <button mat-raised-button color="primary" type="button" (click)="showCreateTask = !showCreateTask">
              <mat-icon>add_task</mat-icon> Add Task
            </button>
            <button mat-stroked-button type="button" (click)="loadTasks()">
              <mat-icon>refresh</mat-icon> Refresh
            </button>
            <mat-spinner *ngIf="loadingTasks()" diameter="24"></mat-spinner>
          </div>

          <mat-card class="add-member-card" *ngIf="showCreateTask">
            <mat-card-header>
              <mat-card-title>New project task</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="task-form">
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Title *</mat-label>
                  <input matInput [(ngModel)]="newTaskTitle" maxlength="300" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Description</mat-label>
                  <textarea matInput rows="2" [(ngModel)]="newTaskDescription"></textarea>
                </mat-form-field>
                <div class="form-row">
                  <mat-form-field appearance="outline">
                    <mat-label>Team type</mat-label>
                    <mat-select [(ngModel)]="newTaskTeamKind" (ngModelChange)="newTaskTeamLeadId = ''">
                      <mat-option value="">Any</mat-option>
                      <mat-option *ngFor="let k of teamKindOptions" [value]="k.value">{{ k.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Team Lead *</mat-label>
                    <mat-select [(ngModel)]="newTaskTeamLeadId">
                      <mat-option *ngFor="let l of filteredLeads()" [value]="l.leadEmployeeId">
                        {{ l.leadName }} · {{ l.teamName }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Priority</mat-label>
                    <mat-select [(ngModel)]="newTaskPriority">
                      <mat-option *ngFor="let p of taskPriorityOptions" [value]="p.value">{{ p.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>
                <div class="form-row">
                  <mat-form-field appearance="outline">
                    <mat-label>Start date</mat-label>
                    <input matInput type="date" [(ngModel)]="newTaskStartDate" />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Deadline *</mat-label>
                    <input matInput type="date" [(ngModel)]="newTaskDueDate" required />
                  </mat-form-field>
                </div>
                <p class="hint" *ngIf="!teamLeads().length">
                  No active team leads found. Create a team with a lead under Organization → Teams first.
                </p>
                <button mat-raised-button color="primary"
                  [disabled]="creatingTask() || !newTaskTitle.trim() || !newTaskDueDate || !newTaskTeamLeadId"
                  (click)="createTask()">
                  {{ creatingTask() ? 'Creating…' : 'Create Task' }}
                </button>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="add-member-card" *ngIf="showReportBug">
            <mat-card-header>
              <mat-card-title>Report bug against task</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="task-form">
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Task *</mat-label>
                  <mat-select [(ngModel)]="bugTaskId">
                    <mat-option *ngFor="let t of tasks()" [value]="t.id">{{ t.title }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Bug title *</mat-label>
                  <input matInput [(ngModel)]="bugTitle" maxlength="300" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Description</mat-label>
                  <textarea matInput rows="2" [(ngModel)]="bugDescription"></textarea>
                </mat-form-field>
                <div class="form-row">
                  <mat-form-field appearance="outline">
                    <mat-label>Severity</mat-label>
                    <mat-select [(ngModel)]="bugSeverity">
                      <mat-option *ngFor="let s of bugSeverityOptions" [value]="s.value">{{ s.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Category</mat-label>
                    <mat-select [(ngModel)]="bugCategory">
                      <mat-option *ngFor="let c of bugCategoryOptions" [value]="c.value">{{ c.label }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>
                <mat-form-field appearance="outline" class="full">
                  <mat-label>Steps to reproduce</mat-label>
                  <textarea matInput rows="2" [(ngModel)]="bugSteps"></textarea>
                </mat-form-field>
                <div class="form-row">
                  <button mat-raised-button color="warn"
                    [disabled]="reportingBug() || !bugTaskId || !bugTitle.trim()"
                    (click)="reportBug()">
                    {{ reportingBug() ? 'Reporting…' : 'Report Bug' }}
                  </button>
                  <button mat-button type="button" (click)="showReportBug = false">Cancel</button>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <mat-card class="members-card" *ngIf="tasks().length > 0">
            <table mat-table [dataSource]="tasks()" class="members-table">
              <ng-container matColumnDef="title">
                <th mat-header-cell *matHeaderCellDef>Task</th>
                <td mat-cell *matCellDef="let task">
                  <div class="member-name">{{ task.title }}</div>
                </td>
              </ng-container>
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let task">{{ task.statusLabel || task.status }}</td>
              </ng-container>
              <ng-container matColumnDef="teamLead">
                <th mat-header-cell *matHeaderCellDef>Team Lead</th>
                <td mat-cell *matCellDef="let task">{{ task.teamLeadName || '—' }}</td>
              </ng-container>
              <ng-container matColumnDef="priority">
                <th mat-header-cell *matHeaderCellDef>Priority</th>
                <td mat-cell *matCellDef="let task">{{ priorityLabel(task.priority) }}</td>
              </ng-container>
              <ng-container matColumnDef="deadline">
                <th mat-header-cell *matHeaderCellDef>Deadline</th>
                <td mat-cell *matCellDef="let task">
                  <span [class.overdue-text]="task.isOverdue">{{ task.dueDate || '—' }}</span>
                </td>
              </ng-container>
              <ng-container matColumnDef="bugs">
                <th mat-header-cell *matHeaderCellDef>Bugs</th>
                <td mat-cell *matCellDef="let task">{{ task.bugCount || 0 }}</td>
              </ng-container>
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let task">
                  <button mat-stroked-button color="warn" type="button" (click)="openReportBug(task)">
                    <mat-icon>bug_report</mat-icon> Report bug
                  </button>
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="taskColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: taskColumns;"></tr>
            </table>
          </mat-card>

          <div class="empty-members" *ngIf="!loadingTasks() && tasks().length === 0">
            <mat-icon>task_alt</mat-icon>
            <p>No tasks yet. Add a task with a mandatory deadline for this project.</p>
          </div>

        </div>
      </mat-tab>

      <!-- Dashboard Tab -->
      <mat-tab label="Dashboard">
        <div class="tab-content">
          <div class="tasks-toolbar">
            <a mat-stroked-button [routerLink]="reportsLink()" [queryParams]="reportsQueryParams()">
              <mat-icon>insights</mat-icon> Full reports
            </a>
            <button mat-stroked-button type="button" (click)="loadProjectDashboard(); loadDeadlineWidgets()">
              <mat-icon>refresh</mat-icon> Refresh
            </button>
            <mat-spinner *ngIf="loadingDashboard()" diameter="24"></mat-spinner>
          </div>

          @if (deadlineDash(); as dd) {
            <div class="stats-grid dashboard-stats">
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value">{{ dd.dueToday }}</div>
                  <div class="stat-label">Due today</div>
                </div>
              </mat-card>
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value">{{ dd.dueTomorrow }}</div>
                  <div class="stat-label">Due tomorrow</div>
                </div>
              </mat-card>
              @if (dd.dueThisWeek != null) {
                <mat-card class="stat-card tt-compact-card">
                  <div class="stat-body">
                    <div class="stat-value">{{ dd.dueThisWeek }}</div>
                    <div class="stat-label">Due this week</div>
                  </div>
                </mat-card>
              }
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value overdue-text">{{ dd.overdue }}</div>
                  <div class="stat-label">Overdue</div>
                </div>
              </mat-card>
            </div>
          }

          @if (projectSla(); as sla) {
            <mat-card class="info-card sla-card">
              <mat-card-header><mat-card-title>SLA compliance</mat-card-title></mat-card-header>
              <mat-card-content>
                <div class="stats-grid dashboard-stats">
                  <div class="stat-inline"><span>On-time</span><strong>{{ sla.onTimePercent ?? '—' }}@if (sla.onTimePercent != null) {%}</strong></div>
                  <div class="stat-inline"><span>Target</span><strong>{{ sla.slaTargetPercent ?? '—' }}@if (sla.slaTargetPercent != null) {%}</strong></div>
                  <div class="stat-inline"><span>Avg resolution</span><strong>@if (sla.averageResolutionDays != null) { {{ sla.averageResolutionDays }}d } @else { — }</strong></div>
                  <div class="stat-inline"><span>Status</span><strong>{{ sla.healthLabel || (sla.metSla ? 'Met SLA' : 'At risk') }}</strong></div>
                </div>
              </mat-card-content>
            </mat-card>
          }

          @if (projectDashboard(); as d) {
            @if (dashboardHealthMetrics(); as hm) {
              <mat-card class="info-card health-card">
                <mat-card-header><mat-card-title>Project health</mat-card-title></mat-card-header>
                <mat-card-content>
                  <div class="health-rag-banner" [ngClass]="healthRagClass(hm.healthLabel, hm.healthScore)">
                    <strong>{{ hm.healthLabel || 'Healthy' }}</strong>
                    @if (hm.healthScore != null) {
                      <span>Health score {{ hm.healthScore }}</span>
                    }
                  </div>
                  <div class="stats-grid dashboard-stats health-metrics">
                    <div class="stat-inline">
                      <span>Health score</span>
                      <strong [ngClass]="healthRagClass(hm.healthLabel, hm.healthScore)">{{ hm.healthScore ?? '—' }}</strong>
                    </div>
                    <div class="stat-inline">
                      <span>Risk score</span>
                      <strong>{{ hm.riskScore ?? '—' }}</strong>
                    </div>
                    <div class="stat-inline">
                      <span>Overdue</span>
                      <strong>{{ formatPercent(hm.overduePercent) }}</strong>
                    </div>
                    <div class="stat-inline">
                      <span>Blocked</span>
                      <strong>{{ formatPercent(hm.blockedPercent) }}</strong>
                    </div>
                    <div class="stat-inline">
                      <span>Completion</span>
                      <strong>{{ formatPercent(hm.completionPercent ?? d.completionPercent) }}</strong>
                    </div>
                    <div class="stat-inline">
                      <span>Velocity trend</span>
                      <strong [ngClass]="velocityTrendClass(hm.velocityTrend)">{{ velocityTrendLabel(hm.velocityTrend) }}</strong>
                    </div>
                  </div>
                </mat-card-content>
              </mat-card>
            }

            <div class="stats-grid dashboard-stats">
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value" [ngClass]="healthRagClass(d.healthLabel ?? dashboardHealthMetrics().healthLabel, d.healthScore ?? dashboardHealthMetrics().healthScore)">{{ d.healthLabel ?? dashboardHealthMetrics().healthLabel ?? '—' }}</div>
                  <div class="stat-label">Health</div>
                </div>
              </mat-card>
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value">{{ d.completionPercent ?? p.progress }}%</div>
                  <div class="stat-label">Completion</div>
                </div>
              </mat-card>
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value">{{ d.openIssues ?? p.tasksSummary.open }}</div>
                  <div class="stat-label">Open</div>
                </div>
              </mat-card>
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value">{{ d.completedIssues ?? d.doneIssues ?? p.tasksSummary.done }}</div>
                  <div class="stat-label">Completed</div>
                </div>
              </mat-card>
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value">{{ d.blockedIssues ?? 0 }}</div>
                  <div class="stat-label">Blocked</div>
                </div>
              </mat-card>
              <mat-card class="stat-card tt-compact-card">
                <div class="stat-body">
                  <div class="stat-value overdue-text">{{ d.overdueIssues ?? 0 }}</div>
                  <div class="stat-label">Overdue</div>
                </div>
              </mat-card>
            </div>

            @if (d.sprintProgress; as sp) {
              <mat-card class="info-card">
                <mat-card-header><mat-card-title>Sprint progress — {{ sp.sprintName }}</mat-card-title></mat-card-header>
                <mat-card-content>
                  <p>{{ sp.completedPoints ?? 0 }} / {{ sp.totalPoints ?? 0 }} pts ({{ sp.percentComplete ?? 0 }}%)</p>
                  <div class="progress-bar-large">
                    <div class="progress-fill-large" [style.width.%]="sp.percentComplete ?? 0"></div>
                  </div>
                </mat-card-content>
              </mat-card>
            }

            @if (d.velocity?.sprints?.length) {
              <mat-card class="info-card">
                <mat-card-header><mat-card-title>Velocity (avg {{ d.velocity!.averageCompleted }})</mat-card-title></mat-card-header>
                <mat-card-content>
                  <ul class="timeline-list">
                    @for (s of d.velocity!.sprints; track s.sprintId) {
                      <li>{{ s.name }} — committed {{ s.committedPoints }}, completed {{ s.completedPoints }}</li>
                    }
                  </ul>
                </mat-card-content>
              </mat-card>
            }

            @if (d.upcomingDeadlines?.length) {
              <mat-card class="info-card">
                <mat-card-header><mat-card-title>Upcoming deadlines</mat-card-title></mat-card-header>
                <mat-card-content>
                  <ul class="timeline-list">
                    @for (item of d.upcomingDeadlines!; track item.id) {
                      <li><strong>{{ item.keyOrTitle }}</strong> · {{ item.dueDate }} · {{ item.assigneeName || 'Unassigned' }}</li>
                    }
                  </ul>
                </mat-card-content>
              </mat-card>
            }

            @if (d.recentActivity?.length) {
              <mat-card class="info-card">
                <mat-card-header><mat-card-title>Recent activity</mat-card-title></mat-card-header>
                <mat-card-content>
                  @for (a of d.recentActivity!; track a.occurredAt + a.message) {
                    <div class="activity-row">
                      <strong>{{ a.activityTypeLabel || 'Activity' }}</strong>
                      <span class="muted">{{ a.occurredAt | date:'medium' }} · {{ a.actorName || 'System' }}</span>
                      <p>{{ a.message }}</p>
                      <mat-divider></mat-divider>
                    </div>
                  }
                </mat-card-content>
              </mat-card>
            }
          }
        </div>
      </mat-tab>

      <!-- Roadmap Tab -->
      <mat-tab label="Roadmap">
        <div class="tab-content">
          <div class="roadmap-toolbar" *ngIf="!loadingRoadmap()">
            <a mat-stroked-button [routerLink]="releaseCalendarLink()" [queryParams]="releaseCalendarQueryParams()">
              <mat-icon>calendar_month</mat-icon> Release calendar
            </a>
          </div>
          <mat-spinner *ngIf="loadingRoadmap()" diameter="28"></mat-spinner>

          <mat-card class="add-member-card" *ngIf="!loadingRoadmap()">
            <mat-card-header><mat-card-title>Add milestone</mat-card-title></mat-card-header>
            <mat-card-content>
              <div class="form-row">
                <mat-form-field appearance="outline"><mat-label>Name *</mat-label><input matInput [(ngModel)]="milestoneName"></mat-form-field>
                <mat-form-field appearance="outline"><mat-label>Target date</mat-label><input matInput type="date" [(ngModel)]="milestoneTargetDate"></mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Version</mat-label>
                  <mat-select [(ngModel)]="milestoneVersionId">
                    <mat-option value="">None</mat-option>
                    @for (v of roadmap()?.versions ?? []; track v.id) {
                      <mat-option [value]="v.id">{{ v.name }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
              <mat-form-field appearance="outline" class="full">
                <mat-label>Description</mat-label>
                <textarea matInput rows="2" [(ngModel)]="milestoneDescription"></textarea>
              </mat-form-field>
              <button mat-raised-button color="primary" [disabled]="savingMilestone() || !milestoneName.trim()" (click)="createMilestone()">
                {{ savingMilestone() ? 'Saving…' : 'Add milestone' }}
              </button>
            </mat-card-content>
          </mat-card>

          @if (roadmap(); as rm) {
            <app-pm-timeline-view [projectId]="projectId" [showShare]="true"></app-pm-timeline-view>

            <div class="roadmap-timeline">
              @for (v of rm.versions; track v.id) {
                <div class="timeline-item version-item">
                  <div class="timeline-marker version"></div>
                  <div class="timeline-content">
                    <strong>{{ v.name }}</strong>
                    <span class="muted">{{ v.statusLabel }}</span>
                    @if (v.deploymentStatus != null || v.deploymentStatusLabel) {
                      <span class="deploy-badge">{{ deploymentLabel(v.deploymentStatus, v.deploymentStatusLabel) }}</span>
                    }
                    @if (v.startDate || v.releaseDate) {
                      <p class="muted">{{ v.startDate || '—' }} → {{ v.releaseDate || 'TBD' }}</p>
                    }
                    @if (v.progressPercent != null || v.issueCount) {
                      <p>{{ v.completedIssueCount ?? 0 }}/{{ v.issueCount }} issues@if (v.progressPercent != null) { · {{ v.progressPercent }}%}</p>
                    }
                    @if (v.releaseNotes) { <p>{{ v.releaseNotes }}</p> }
                  </div>
                </div>
              }
              @for (m of rm.milestones; track m.id) {
                <div class="timeline-item milestone-item">
                  <div class="timeline-marker milestone"></div>
                  <div class="timeline-content">
                    <div class="milestone-head">
                      <strong>{{ m.name }}</strong>
                      <button mat-icon-button color="warn" type="button" (click)="deleteMilestone(m)"><mat-icon>delete</mat-icon></button>
                    </div>
                    @if (m.targetDate) { <p class="muted">{{ m.targetDate | date:'mediumDate' }}</p> }
                    @if (m.versionName) { <p>Version: {{ m.versionName }}</p> }
                    @if (m.description) { <p>{{ m.description }}</p> }
                  </div>
                </div>
              } @empty {
                @if (!rm.versions.length) {
                  <p class="muted">No roadmap items yet. Add versions in Advanced PM or milestones above.</p>
                }
              }
            </div>
          }
        </div>
      </mat-tab>

    </mat-tab-group>

  </ng-container>

</div>
